import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { Toaster as Sonner, toast as sonnerToast } from "sonner"
import { useCommunicationHost } from "../../lib/overlayPortal"
import { detailToText } from "../../lib/detailText"

// 跟随 ALIGCUS 主题（html.dark + lib/theme.js 的 ali-theme 事件），不依赖 next-themes Provider
const readTheme = () => (typeof document !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light")

/**
 * 把提示内容规整成 React 能安全渲染的东西（2026-10-22 修复整页白屏）。
 *
 * 修改原因（线上事故）：用户在设计项目详情上传文件，连续三次都整页白屏。
 * 用 Playwright 复现后拿到真实异常：
 *   `Objects are not valid as a React child (found: object with keys
 *    {error, message, currentKey, currentName, stageKey})`，
 * 异常发生在提示卡内部，React 直接卸载了整棵树（`#root` 子节点变 0）。
 *
 * 触发链条：后端「阶段顺序门禁」用的是 `HTTPException(400, detail={...})`
 * —— detail 是**字典**（design_v3._assert_stage_in_turn、design_module 的
 * stage_close 各一处）。而全站约 1200 处 catch 写的是
 * `toast.error(e?.response?.data?.detail || e.message)`，上传接口正是其中一员。
 * 平时 detail 是字符串，这一路相安无事；一旦被顺序门禁拦下，交到 toast 手里的
 * 就是个对象，于是渲染期抛错、整页消失。用户看到的是「一上传就白屏」，
 * 完全看不出真实原因（「流程还没走到本阶段」）—— 比报错更糟的是连报错都看不见。
 *
 * 2026-10-22 二次事故：同一批 detail 还会经 `setErr(...)` / `setD({error})`
 * 存进 state 再渲染进 JSX，那是**另一条**白屏通道，toast 封装管不到。
 * 现已三路一起堵（见 lib/detailText.js）：
 *   ① toast 封装（本文件）
 *   ② lib/api.js 拦截器给 error 挂 `detailText`
 *   ③ 渲染处改用 errText() 兜底
 *
 * 最终效果：对象 detail 被摊平成一句人能读的话（优先 message，其次把已知字段拼起来，
 * 最后兜底 JSON），提示卡正常显示，页面不再崩。
 *
 * 关键取舍：在**封装层**统一兜底，而不是去改 1200 处调用点。
 *   · 调用点太多且会继续新增，靠人工逐个改必然漏；
 *   · 兜底放在这里，任何页面任何接口返回结构化 detail 都不会再炸掉整页；
 *   · React 元素 / 数组（少数调用点传 JSX）原样放行，不影响既有用法。
 *
 * 实现已抽到 lib/detailText.js（detailToText），三处通道共用一份，避免各自漂移。
 */
const flattenToastContent = detailToText

/**
 * 带兜底的 toast。
 *
 * 覆盖 sonner 的全部入口（success / error / info / warning / message / loading /
 * custom / promise / dismiss），保证无论走哪个方法，结构化内容都会被先摊平。
 * 其余属性（id / duration / action / description 等）原样透传。
 */
const wrapToast = (fn) => (content, opts, ...rest) => {
  // custom 的入参是渲染函数 / 元素，不是文案，不能摊平。
  const nextOpts = opts && typeof opts === "object" && "description" in opts
    ? { ...opts, description: flattenToastContent(opts.description) }
    : opts
  const args = nextOpts === undefined && rest.length === 0 ? [] : [nextOpts, ...rest]
  return fn(flattenToastContent(content), ...args)
}

const toast = Object.assign(
  wrapToast(sonnerToast),
  {
    success: wrapToast(sonnerToast.success),
    error: wrapToast(sonnerToast.error),
    info: wrapToast(sonnerToast.info),
    warning: wrapToast(sonnerToast.warning),
    message: wrapToast(sonnerToast.message),
    loading: wrapToast(sonnerToast.loading),
    // custom 收到的是组件 / 渲染函数，透传；promise 内部自己处理文案。
    custom: sonnerToast.custom,
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    getHistory: sonnerToast.getHistory,
    getToasts: sonnerToast.getToasts,
  },
)

/**
 * 全局提示（sonner）封装。
 *
 * 需求（2026-10-21 用户）：「右上角提醒增加关闭，现在悬浮很久遮挡操作」。
 * 原来提示只能等自动消失，而 sonner 在鼠标悬停时会暂停倒计时，提示会一直挂在右上角
 * 盖住顶栏的按钮，用户没法主动关掉。现在给所有提示默认加一个关闭按钮：
 * 整体可用 closeButton={false} 关掉，单条提示也可用 toast.x(msg, { closeButton: false }) 去掉。
 *
 * 2026-10-22 用户补充：「关闭应该在每个提示窗右上方」，并指出上一版实际看不到按钮。
 * 原因见下面 closeButton 类名处的注释 —— 按钮原本挂在卡片外侧、被裁掉了。
 * 现在按钮固定落在卡片内部右上角，任何形态的提示（普通 / richColors 彩色 / 深色 /
 * 带「查看」动作按钮）都能看到并点到。
 */
const Toaster = ({
  // 关闭按钮默认开启（需求见上）；显式传 false 可整体关闭。
  closeButton = true,
  toastOptions,
  ...props
}) => {
  const [theme, setTheme] = useState(readTheme)
  const communicationHost = useCommunicationHost()
  useEffect(() => {
    const h = () => setTheme(readTheme())
    window.addEventListener("ali-theme", h)
    const mo = typeof MutationObserver !== "undefined" ? new MutationObserver(h) : null
    mo?.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    return () => { window.removeEventListener("ali-theme", h); mo?.disconnect() }
  }, [])

  const notifications = (
    <Sonner
      theme={theme}
      className="toaster group"
      closeButton={closeButton}
      toastOptions={{
        // 调用方传入的其它 toastOptions 先铺开，再由下面三项覆盖 / 合并，
        // 保证关闭按钮始终存在（否则调用方一传 toastOptions 就把关闭能力抹掉了）。
        ...toastOptions,
        classNames: {
          // pr-9（36px）给右上角的关闭按钮留出净空，避免标题 / 正文压在按钮下面。
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg !pr-9",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          /**
           * 关闭按钮位置：卡片**内部**右上角（2026-10-22 修正）。
           *
           * 修改原因：上一版（提交 2ee3bb26）把按钮放在卡片右上角**外侧**——
           * sonner 靠 `translate(35%, -35%)` 把它顶出卡片边界。只要卡片贴近容器
           * 或可视区上沿（聊天消息提示、上传失败提示这类常驻提示就是这种情况），
           * 露在外面的那半个圆就被裁掉，用户看到的提示「没有关闭按钮」，
           * 于是只能干等倒计时，又回到「悬浮很久遮挡操作」的老问题。
           *
           * 最终效果：按钮整体落在卡片内边距里（right/top 各 6px，24x24），
           * 无论提示在哪、亮色还是深色、几行文字、有没有「查看」动作按钮，
           * 每张提示卡右上角都能看到一个可点的关闭按钮。
           *
           * 关键取舍：
           * - 全部加 `!`（important）：sonner 自身的 `[data-close-button]` 规则
           *   （left/top/transform/width/height）特异度高于普通 Tailwind 工具类，
           *   不加 important 会被它盖回去、按钮又跑到外侧。
           * - `!translate-x-0 !-translate-y-0` 必须显式写：只改 right/top 而不清掉
           *   平移，按钮仍会按 35% 往右上飘出卡片。
           * - 颜色 / 边框继续交给 sonner 主题规则（含 richColors 彩色提示），
           *   这里只定位置和尺寸，避免破坏深色模式配色。
           */
          closeButton:
            "!left-auto !right-1.5 !top-1.5 !h-6 !w-6 !translate-x-0 !-translate-y-0 !shadow-sm",
          ...toastOptions?.classNames,
        },
        // 关闭按钮供读屏使用的中文名（项目默认中文界面）
        closeButtonAriaLabel: toastOptions?.closeButtonAriaLabel ?? "关闭提示",
      }}
      {...props} />
  );
  // 沟通窗置顶后，发送失败等反馈也要可见；登录页等无沟通窗页面保持原布局。
  return communicationHost ? createPortal(notifications, communicationHost) : notifications;
}

export { Toaster, toast }
